// @flow
import {
  Box,
  Container,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { InView } from 'react-intersection-observer';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { OutboundLink } from 'gatsby-plugin-google-analytics';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { motion } from 'framer-motion';
import * as React from 'react';

import Markdown from './styled/markdown';
import Navigation from './shared/navigation';
import Newsletter from './shared/newsletter';
import PaymentIcon from './shared/payment-icon';
import SocialIcon from './shared/social-icon';
import WhiteSpace from './styled/white-space';
import footerImg from '../images/footer.png';
import logo from '../images/logo.svg';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(10, 0),
    zIndex: 20,
  },
  text: {
    color: theme.motorX.darkGray,
  },
  linkText: {
    color: theme.motorX.darkGray,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  footerText: {
    color: theme.motorX.darkGray,
    textTransform: 'uppercase',
    paddingLeft: 15,
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  separator: {
    borderRadius: 0,
    borderTop: `10px solid ${theme.palette.secondary.main}`,
    width: '100%',
  },
  socialBox: {
    '&:first-child': {
      paddingLeft: 0,
    },
    paddingLeft: theme.spacing(1),
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  footerImg: {
    maxWidth: '80%',
  },
}));

const FOOTER_QUERY = graphql`
  query footerSectionQuery {
    allContentfulPolitica {
      nodes {
        nombre
        slug
        id
      }
    }

    contentfulInformacion {
      sucursales {
        id
        correoElectronico
        telefonos
      }
      redesSociales {
        id
        nombre
        url
      }
      metodosDePago {
        nombre
        url
        id
      }
      descripcion
      pieDePagina {
        pieDePagina
      }
    }
  }
`;

export default function Footer() {
  const { contentfulInformacion, allContentfulPolitica } =
    useStaticQuery(FOOTER_QUERY);
  const classes = useStyles();
  const theme = useTheme();

  const variants = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  };

  return (
    <InView threshold={0.02}>
      {({ inView, ref }) => (
        <motion.div
          animate={inView ? 'visible' : 'hidden'}
          ref={ref}
          variants={variants}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <hr className={classes.separator} />
          <div className={classes.root}>
            <Container maxWidth="xl">
              <Grid container spacing={6}>
                <Grid item lg={4}>
                  <img alt="Motor-X Logo" src={logo} />
                  <WhiteSpace size="1rem" />
                  <Markdown className="gray" color={theme.motorX.darkGray}>
                    {contentfulInformacion?.pieDePagina?.pieDePagina}
                  </Markdown>
                  <img
                    src={footerImg}
                    className={classes.footerImg}
                    alt="Pie de página - Motor X"
                  />
                </Grid>
                <Grid container item lg={4}>
                  <Grid item xs={12} lg={6}>
                    <Navigation />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Typography gutterBottom paragraph className={classes.text}>
                      Contacto
                    </Typography>
                    <Typography
                      gutterBottom
                      paragraph
                      className={classes.linkText}
                    >
                      <a
                        href={`mailto:${contentfulInformacion?.sucursales[0].correoElectronico}`}
                      >
                        <strong>
                          {
                            contentfulInformacion?.sucursales[0]
                              .correoElectronico
                          }
                        </strong>
                      </a>
                    </Typography>

                    {contentfulInformacion?.sucursales[0].telefonos.map(
                      (tel) => (
                        <Typography key={tel} className={classes.linkText}>
                          <a href={`tel:${tel}`}>
                            <strong>{tel}</strong>
                          </a>
                        </Typography>
                      ),
                    )}
                    <Box mt={2} display="flex" flexDirection="row">
                      {contentfulInformacion?.redesSociales.map(
                        ({ id, nombre, url }) => (
                          <Box key={id} className={classes.socialBox}>
                            <OutboundLink
                              target="_blank"
                              rel="noreferer noopener"
                              href={url}
                            >
                              <SocialIcon
                                name={nombre}
                                title={nombre}
                                size="2rem"
                                color={theme.motorX.darkGray}
                              />
                            </OutboundLink>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Newsletter />
              </Grid>
            </Container>
            <Container maxWidth="lg">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center">
                    {allContentfulPolitica.nodes.map((policy) => (
                      <Typography
                        component="span"
                        variant="caption"
                        className={classes.footerText}
                        key={policy.id}
                      >
                        <Link to={`/policies/${policy.slug}`}>
                          {policy.nombre}
                        </Link>
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Container>
            <Container maxWidth="lg">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" component="div" justifyContent="center">
                    {contentfulInformacion?.metodosDePago.map((method) => (
                      <IconButton>
                        <PaymentIcon
                          size="3.5rem"
                          name={method.nombre}
                          color={theme.palette.secondary.main}
                        />
                      </IconButton>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </div>
        </motion.div>
      )}
    </InView>
  );
}
